import { Fragment, useMemo, useState } from "react";
import { IonContent, IonPage } from "@ionic/react";
import { useHistory } from "react-router-dom";

import { ActionBar } from "../components/ActionBar";
import { ExerciseRow } from "../components/ExerciseRow";
import { ExerciseSheet } from "../components/ExerciseSheet";
import { Hero } from "../components/Hero";
import { I } from "../components/icons";
import { Ring } from "../components/Ring";
import { NavRow, RowGroup } from "../components/Rows";
import { Screen } from "../components/Shell";
import { TopBar } from "../components/TopBar";
import { dayLabel, todayStr } from "../domain/date";
import { moveById } from "../domain/moves";
import { playerMode } from "../domain/playerView";
import { doseIndex, doseText, sessionFor, sessionKeyFor, typeName } from "../domain/plan";
import { fillTemplate, programmeWeekNo, type FilledSession } from "../domain/programme";
import { blockWhere } from "../domain/programmeView";
import { BLOCK_REST_SECS, buildQueue, dayQueue, queueMinutes, sessionPct, STAND_UP_LABEL, STAND_UP_SECS, storeKey } from "../domain/queue";
import { SESSION_LABEL } from "../domain/session";
import { isSteadyKey } from "../domain/templates";
import type { Exercise, SteadyKey } from "../domain/types";
import { sessionProgress } from "../domain/week";
import { useCycleBar, type CycleBar } from "../lib/useCycleBar";
import { useDeviceAnswers, useKaya, usePlayerMode, useTrainingDevice } from "../store/kayaStore";
import { sessionGate } from "../domain/trainingQuestions";
import { usePlayerIntent } from "../store/playerIntent";
import "./Train.css";

/**
 * Today's Steady session (board TrainSessionSteady, ADR-052): the template as the filler built it
 * for this person, block by block, each swapped move with its reason on a muted line, the "Stand up
 * slowly" rest where a seated move leads to a standing one, and Start or the short version. Minutes,
 * never kcal (plan §3.4). With `extra` it is that template as an extra (board TrainSessionYoga,
 * ADR-067): the same listing, ticks from its own `extra:` bucket, Start runs it as an extra, and
 * there is no short version or cycle line — it is never the day's session.
 */
export default function TrainSessionSteady({ extra }: { extra?: SteadyKey } = {}) {
  const S = useKaya((s) => s.S);
  const meta = useKaya((s) => s.meta);
  // the player's language reaches the overview too, as the moves' Hindi names (ADR-055)
  const hi = playerMode(S.profile.training, meta).lang === "hi";
  const session = useKaya((s) => s.session);
  const history = useHistory();
  const intent = usePlayerIntent();
  const device = useTrainingDevice();
  const answers = useDeviceAnswers();
  const pm = usePlayerMode();
  const [shown, setShown] = useState<{ ex: Exercise; note?: string } | null>(null);

  const today = todayStr();
  const gate = sessionGate(S, extra ?? sessionKeyFor(S, today), answers);
  const key = gate.key;
  // A day already begun lists what it froze: its flags and whether it ran short (ADR-050, ADR-052).
  // An extra freezes nothing; it is rebuilt as the player rebuilds it.
  const dq = useMemo(() => (extra ? { queue: null, ran: null } : dayQueue(S, today, key)), [extra, S, today, key]);
  const fill = useMemo(
    () => (dq.ran ? { flags: dq.ran.flags, short: dq.ran.short } : { device }),
    [dq, device]
  );
  const filled: FilledSession | null = useMemo(
    () => (isSteadyKey(key) ? fillTemplate(S, key, fill) : null),
    [S, key, fill]
  );
  const s = filled ?? sessionFor(key, S);
  const p = dq.ran?.phase ?? doseIndex(S, key, today);
  const queue = useMemo(() => dq.queue ?? buildQueue(S, key, p, fill), [dq, S, key, p, fill]);
  const short = useMemo(() => buildQueue(S, key, p, { device, short: true }), [S, key, p, device]);
  const done: Record<number, 1> = extra
    ? (S.logs[today]?.done?.[storeKey(key, { extra: true })] ?? {})
    : dq.queue
      ? (S.logs[today]?.done?.[key] ?? {})
      : {};
  const workIdx = queue.flatMap((q, qi) => (q.kind === "rest" ? [] : [qi]));
  const extraProg = { done: workIdx.filter((qi) => done[qi]).length, total: workIdx.length };
  const pct = extra ? (extraProg.total ? extraProg.done / extraProg.total : 0) : (sessionPct(S, today, key) ?? 0);
  const prog = extra ? extraProg : sessionProgress(S, today, key);
  const mins = queueMinutes(S, queue, pm.simple);
  const moves = queue.filter((q) => q.kind !== "rest").length;
  const resumeLabel = session ? `Resume ${SESSION_LABEL[session.kind].toLowerCase()}` : null;

  const player = (route: string) => history.push(route, { from: history.location.pathname });
  const start = () => {
    if (session) return player(session.route);
    if (extra) intent.setExtra(key);
    player("/workout");
  };
  const startShort = () => {
    if (session) return player(session.route);
    intent.setShort();
    player("/workout");
  };

  const primary = {
    disabled: gate.stop,
    reason: gate.stop ? (gate.line ?? undefined) : undefined,
    label:
      session && session.kind !== "workout"
        ? (resumeLabel ?? "Resume")
        : pct >= 1
          ? "Done — view"
          : pct > 0 && prog
            ? `Continue · ${prog.done} of ${prog.total}`
            : "Start",
    onClick: start,
    icon: pct >= 1 ? I.check : I.play,
  };
  const begun = pct > 0 || !!session;
  // the cycle line above Start (ADR-057); its offer or Keep my session takes the Short version's place
  const dayBar = useCycleBar(today, mins, start);
  const cycleBar: CycleBar = extra ? {} : dayBar;

  return (
    <IonPage>
      <IonContent>
        <Screen>
          <div className="train-page">
            <TopBar title={extra ? "Any day" : "Today’s session"} back={{ to: "/train", label: "Back to Train" }} />

            <Hero
              label={extra ? `${dayLabel(today)} · an extra, never a missed day` : `${dayLabel(today)} · Steady, week ${programmeWeekNo(S, today)}`}
              title={typeName(key)}
              line={`${mins} min · ${moves} moves · every rest seated`}
              sub={filled?.lead ?? undefined}
              visual={
                pct > 0 ? (
                  <Ring pct={pct} size={72} stroke={7} color="var(--brand)" figure={prog ? `${prog.done}/${prog.total}` : null} />
                ) : undefined
              }
              visualSide
            />

            <p className="train-fine">{s.how} Tap any move for how to do it.</p>

            {s.blocks.map((b, bi) => {
              const R = b.rounds[p];
              const rm = S.profile.restMove || b.rest[p];
              const where = blockWhere(b, bi === s.blocks.length - 1);
              return (
                <RowGroup
                  key={`${b.title}:${bi}`}
                  heading={
                    <>
                      {b.title}
                      <span className="train-hsub">
                        {R > 1 ? `${R} rounds` : where} · rest {rm} s
                      </span>
                    </>
                  }
                >
                  {b.standUp ? (
                    <NavRow
                      kind="value"
                      title={STAND_UP_LABEL}
                      subtitle="A hand on the counter until you feel steady"
                      icon={I.clock}
                      value={`${Math.max(STAND_UP_SECS, BLOCK_REST_SECS)} s`}
                    />
                  ) : null}
                  {b.items.map((it, ii) => {
                    const ex = moveById(it.id);
                    if (!ex) return null;
                    const idxs: number[] = [];
                    queue.forEach((q, qi) => {
                      if (q.kind !== "rest" && q.bi === bi && q.idx === ii + 1) idxs.push(qi);
                    });
                    const dn = idxs.filter((x) => done[x]).length;
                    const words = [it.cue, it.note].filter(Boolean).join(" · ");
                    return (
                      <Fragment key={`${it.id}:${ii}`}>
                        {/* On the first move `standUp` marks the rest between two rounds (a block ending seated and
                           starting standing): the listing shows a block's moves once, so that rest is the player's alone; the rest into a
                           block from a seated one is `b.standUp`, above. The cool-down runs one round, so its calf
                           stretch after the standing balance block needs none. */}
                        {it.standUp && ii > 0 ? (
                          <NavRow
                            kind="value"
                            title={STAND_UP_LABEL}
                            subtitle="A hand on the counter until you feel steady"
                            icon={I.clock}
                            value={`${Math.max(STAND_UP_SECS, rm)} s`}
                          />
                        ) : null}
                        <div className="train-ex">
                          <ExerciseRow
                            ex={ex}
                            dose={doseText(it, p)}
                            frac={idxs.length ? dn / idxs.length : 0}
                            hipSafe={S.profile.hipSafe}
                            hi={hi}
                            onOpen={(e) => setShown({ ex: e, note: words || undefined })}
                          />
                          {words ? <p className="train-note">{words.charAt(0).toUpperCase() + words.slice(1)}</p> : null}
                        </div>
                      </Fragment>
                    );
                  })}
                </RowGroup>
              );
            })}

            {filled && filled.dropped.length ? (
              <p className="train-fine">Left out today: {filled.dropped.map((d) => d.note).join("; ")}.</p>
            ) : null}

            <ActionBar
              primary={primary}
              secondary={
                cycleBar.secondary ?? (!extra && !begun && short.length ? { label: "Short version", onClick: startShort } : undefined)
              }
              lead={gate.stop ? undefined : (gate.line ?? cycleBar.lead)}
            />
          </div>
        </Screen>
        {shown ? (
          <ExerciseSheet ex={shown.ex} hipSafe={S.profile.hipSafe} note={shown.note} onClose={() => setShown(null)} />
        ) : null}
      </IonContent>
    </IonPage>
  );
}
